import type {
  CommodityRelatedOption,
  CommodityRelatedOptionCode,
  ProductCodeType,
} from '@/pages/order-center/type';
import {
  readOptionsMap,
  writeProductOption,
} from '@/services/commodity-options-storage';
import type { ProductOptionsStatus } from '@/services/commodity-options-storage';
import { useState, useCallback, useRef } from 'react';

/**
 * 商品附加选项足迹 Model
 *
 * 职责：按「用户 × 商品 × optionCode」维度管理用户拨动过的开关状态。
 * - 只有用户主动操作过的开关才会写入 store / local；
 * - 未操作过的开关读取时回落到计价接口下发的 `selected` 默认值；
 * - 灰度开关 `commodity_related_options_last_selected`：
 *   - ON：进页时把 local 全量灌入 store；用户操作时写 store + local；
 *   - OFF：不读 local；用户操作时只写 store。
 */
export default function OrderCenterCommodityFootprint() {
  /**
   * 内存 store：productCode -> 被用户操作过的 optionCode 状态。
   * 没有被用户操作过的 productCode / optionCode 不会出现在这里。
   */
  const [commodityRelatedOptionsStatus, setCommodityRelatedOptionsStatus] = useState<
    Record<string, ProductOptionsStatus>
  >({});

  // ---- 足迹开关 & 用户标识 ----
  const footprintUserIdRef = useRef<string>('');
  const [footprintEnabled, setFootprintEnabled] = useState<boolean>(false);

  /**
   * 页面初始化时由外部调用，传入当前用户 ID 和灰度开关状态。
   * 灰度 ON 且 userId 存在时，立刻把该用户的 local 全量灌入 store。
   */
  const setFootprintConfig = useCallback(
    (config: { userId: string; enabled: boolean }) => {
      const { userId, enabled } = config;
      footprintUserIdRef.current = userId || '';
      setFootprintEnabled(enabled);

      if (enabled && userId) {
        const localMap = readOptionsMap(userId);
        if (localMap) {
          // 用 local 覆盖当前 store；上一会话中未操作的 code 在 local 里也不会有 key
          setCommodityRelatedOptionsStatus(localMap);
        }
      }
    },
    [],
  );

  /**
   * 用户切换某个商品下的单个附加选项：
   * - 写 store：仅覆盖对应 (productCode, optionCode) 的布尔值，不影响其他 code；
   * - 同步写入 local。
   */
  const updateCommodityRelatedOptions = useCallback(
    (
      productCode: ProductCodeType | undefined,
      optionCode: CommodityRelatedOptionCode,
      checked: boolean,
    ) => {
      if (!productCode || !optionCode) return;
      setCommodityRelatedOptionsStatus((prev) => ({
        ...prev,
        [productCode]: {
          ...(prev[productCode] || {}),
          [optionCode]: checked,
        },
      }));
      const userId = footprintUserIdRef.current;
      // 不管灰度开关是否开启，只要用户手动操作过，则写入本地
      if (userId) {
        writeProductOption(userId, productCode, optionCode, checked);
      }
    },[]);

  /**
   * 合并 store 与计价接口默认值，给出当前商品下「真实勾选的 code 列表」。
   * 规则：
   * - store 里有对应 optionCode 的布尔值 -> 用 store
   * - store 里没有 -> 用接口 `selected`
   */
  const resolveSelectedCodes = useCallback(
    (
      productCode: string | undefined,
      options?: CommodityRelatedOption[],
    ): CommodityRelatedOptionCode[] => {
      if (!productCode || !options?.length) return [];
      const overrides = commodityRelatedOptionsStatus[productCode] || {};
      return options
        .map((opt) => {
          const override = overrides[opt.code];
          // 如果 store 里有对应 optionCode 的布尔值，则用 store 里的值
          // 如果 store 里没有，则用接口 `selected` 的值
          const checked = typeof override === 'boolean' ? override : !!opt.selected;
          return checked ? opt.code : null;
        })
        .filter((c): c is CommodityRelatedOptionCode => !!c);
    },
    [commodityRelatedOptionsStatus],
  );

  return {
    /** 全量 store：productCode -> { optionCode: boolean }，仅含用户操作过的 code */
    commodityRelatedOptionsStatus,
    /** 设置足迹开关配置（userId + enabled），页面初始化时调用 */
    setFootprintConfig,
    /** 足迹开关是否开启 */
    footprintEnabled,
    /** 切换某个商品下单个附加选项 */
    updateCommodityRelatedOptions,
    /** 合并 store 与接口默认，得到当前商品已勾选的 code 列表 */
    resolveSelectedCodes,
  };
}
